import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { GridRequest } from "../../../../shared/grid/models/grid-request.model";
import { StringHelper } from "../../../../utilities/contracts/string-helper";
import { BulkPendInputType } from "../bulk-updates/bulk-pend-input-type.enum";
import { IBulkActionJobSummary } from "./bulk-action-job-summary.model";
import { BulkActionResultsService } from "./bulk-action-results.service";

/**
 * Bulk-action results page reached from a job-completion notification
 * (route: /bulkactions/bulkactionresults/:jobId?phase=Validate|Execute).
 * Shows the validation/execute summary and a paged per-chase results grid.
 */
@Component({
    selector: "app-bulk-action-results",
    templateUrl: "./bulk-action-results.component.html",
    styleUrls: ["./bulk-action-results.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkActionResultsComponent implements OnInit, OnDestroy {
    jobId: string;
    phase: string;
    summary: IBulkActionJobSummary;
    gridConfiguration: GridConfiguration;
    gridRequest: GridRequest;
    gridRefresh = new EventEmitter<null>();
    isExecuting = false;
    showExecuteConfirm = false;
    private isFirstLoad = true;
    private readonly sink = new SubSink();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly service: BulkActionResultsService,
        private readonly messagingService: MessagingService,
        private readonly changeDetector: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // React to phase (query-param) changes even when Angular reuses this component instance —
        // e.g. clicking the Execute notification while already on the Validate results page.
        this.sink.add(this.route.queryParamMap.subscribe(params => {
            this.jobId = this.route.snapshot.paramMap.get("jobId");
            const phaseParam = params.get("phase");
            this.phase = StringHelper.isAvailable(phaseParam) ? phaseParam : "Validate";

            // Build the grid AFTER the summary resolves — its columns depend on the job's action
            // (Move → workflow status, Reassign → assigned user).
            this.loadSummary();
            this.changeDetector.markForCheck();
        }));
    }

    ngOnDestroy(): void {
        this.sink.unsubscribe();
    }

    get isExecutePhase(): boolean {
        return (this.phase || "").toLowerCase() === "execute";
    }

    /**
     * The action as a person should read it. The summary carries the raw ActionType, which is an
     * internal identifier — rendering it put "ActivateChases" and "PendChangesUI" in front of the
     * user, in the banner and in the execute confirmation. The identifiers can't change (they key the
     * worker's handler lookup), so the display name is mapped here.
     *
     * An unmapped action falls back to the raw ActionType rather than to a generic word: if someone
     * adds an action and forgets this map, an odd-looking label is more useful than a vague one.
     */
    get actionDisplayName(): string {
        switch ((this.summary?.actionType || "").toLowerCase()) {
            case "move": return "Move";
            case "reassign": return "Reassign";
            case "activatechases": return "Activate";

            // Both pend actions read as "Pend" — the UI/SFTP split is an implementation detail, and
            // only the UI one has a results page a person opens.
            case "pendchangesui":
            case "pendchanges": return "Pend";

            // Distinct from "Pend" above: those CREATE pends, this one CHANGES existing ones. The
            // sub-action is what the user actually chose, so it carries the label when present.
            case "pendupdateui": return this.pendUpdateDisplayName;

            case "tagchases": return "Tag";

            default: return this.summary?.actionType || "";
        }
    }

    /** "Pend Update — Close" and friends; plain "Pend Update" if the sub-action is missing. */
    private get pendUpdateDisplayName(): string {
        switch ((this.summary?.actionSubType || "").toLowerCase()) {
            case "resolve": return "Pend Update — Resolve";
            case "close": return "Pend Update — Close";
            case "changeownertoclient": return "Pend Update — Change Owner to Client";
            case "changeownertoso": return "Pend Update — Change Owner";
            default: return "Pend Update";
        }
    }

    /** Whether this job's ids were pend ids rather than chase ids (pend-update only). */
    private get isPendIdInput(): boolean {
        return this.summary?.pendChaseInputType === BulkPendInputType.PendId;
    }

    /**
     * Whether Execute has already been triggered for this job (queued, running, completed or
     * failed). The Validate results page stays reachable forever from its notification, so a user
     * can come back to it long after executing — this is what stops a second run.
     */
    get isExecuteStarted(): boolean {
        return !!this.summary && StringHelper.isAvailable(this.summary.executeStatus);
    }

    /** Why the Execute button is disabled, shown next to it. Empty when it's clickable. */
    get executeDisabledReason(): string {
        if (!this.summary || this.canExecute || this.isExecutePhase) {
            return "";
        }

        if (this.isExecuteStarted) {
            return `This bulk action was already executed (${this.summary.executeStatus}). Start a new bulk action to run it again.`;
        }

        if ((this.summary.status || "").toLowerCase() !== "completed") {
            return "Validation has not finished yet.";
        }

        if (this.summary.success < 1) {
            return "No chases passed validation, so there is nothing to execute.";
        }

        return "";
    }

    /**
     * Execute is offered only on a completed Validate phase that has at least one passed chase and
     * has not already been executed.
     */
    get canExecute(): boolean {
        return !this.isExecutePhase
            && !!this.summary
            && (this.summary.status || "").toLowerCase() === "completed"
            && this.summary.success > 0
            && !this.isExecuteStarted
            && !this.isExecuting;
    }

    openExecuteConfirm(): void {
        this.showExecuteConfirm = true;
    }

    execute(): void {
        this.isExecuting = true;
        this.service.executeJob(this.jobId).subscribe(
            () => {
                this.messagingService.showToast(
                    "Execute started. You'll be notified when it completes.", SeverityType.SUCCESS);
                // Re-read the summary so executeStatus reflects the now-queued Execute phase — that,
                // not the in-memory isExecuting flag, is what keeps the button disabled on a reload.
                this.loadSummary();
                this.changeDetector.markForCheck();
            },
            () => {
                this.isExecuting = false;
                this.messagingService.showToast(
                    "Failed to start Execute. Please try again.", SeverityType.ERROR);
                this.changeDetector.markForCheck();
            });
    }

    private loadSummary(): void {
        this.service.getSummary(this.jobId, this.phase).subscribe(
            summary => {
                this.summary = summary;
                this.buildGrid();
            },
            () => {
                // Summary failed — still render the grid (default Move columns) so the page isn't stuck.
                this.buildGrid();
            });
    }

    /** (Re)builds the grid once the summary is known; columns vary by action type. */
    private buildGrid(): void {
        this.createGrid();
        if (!this.isFirstLoad) {
            this.gridRefresh.emit();
        }
        this.isFirstLoad = false;
        this.changeDetector.markForCheck();
    }

    /**
     * The columns between "Chase Key" and "Validation Status" are per action, and they mirror what the
     * synchronous Bulk Change grid shows for the same action so a user sees the same thing either way.
     *
     * Keyed on the action explicitly, with Move as the default. This used to be a two-way test against
     * "reassign", which silently gave every other action Move's workflow-status columns — for Activate
     * that meant two permanently blank columns, since its result rows carry no status names.
     */
    private actionColumns(): GridColumnDefinition[] {
        switch ((this.summary?.actionType || "").toLowerCase()) {
            case "reassign":
                return [
                    new GridColumnDefinition({ field: "currentAssignedUser", header: "Current Assigned To User" }),
                    new GridColumnDefinition({ field: "newAssignedUser", header: "New Assigned To User" }),
                ];

            case "activatechases":
                return [
                    new GridColumnDefinition({ field: "isActive", header: "Active(Yes/No)" }),
                ];

            // "Tags Added" arrives already joined (the worker aggregates one row per chase x tag).
            case "tagchases":
                return [
                    new GridColumnDefinition({ field: "measureCode", header: "Measure" }),
                    new GridColumnDefinition({ field: "tagsAdded", header: "Tags Added" }),
                    new GridColumnDefinition({ field: "tagRemoved", header: "Tag Removed" }),
                ];

            // The durable "Pend Chases" action. Owner is the only column the synchronous pend grid
            // adds (Chase / Chase Key / Owner / ValidationStatus / Message), so it is the only one
            // here — pendCode and chasePendId ride on the row for the export, not the grid.
            case "pendchangesui":
                return [
                    new GridColumnDefinition({ field: "owner", header: "Owner" }),
                ];

            // The durable pend-UPDATE action. Which pair to show depends on the SUB-action, exactly
            // as the synchronous grid does (it toggles the same two pairs with `show:` flags): a
            // status change reports old/new status, an owner change reports old/new owner. Showing
            // both pairs would leave two permanently blank columns either way.
            case "pendupdateui":
                return this.isOwnerSubAction
                    ? [
                        new GridColumnDefinition({ field: "oldPendOwner", header: "Old Pend Owner" }),
                        new GridColumnDefinition({ field: "newPendOwner", header: "New Pend Owner" }),
                    ]
                    : [
                        new GridColumnDefinition({ field: "oldPendStatus", header: "Old Pend Status" }),
                        new GridColumnDefinition({ field: "newPendStatus", header: "New Pend Status" }),
                    ];

            default:
                return [
                    new GridColumnDefinition({ field: "currentWorkflowStatus", header: "Current Workflow Status" }),
                    new GridColumnDefinition({ field: "newWorkflowStatus", header: "New Workflow Status" }),
                ];
        }
    }

    /** Whether the pend-update sub-action changes the owner rather than the status. */
    private get isOwnerSubAction(): boolean {
        const subType = (this.summary?.actionSubType || "").toLowerCase();
        return subType === "changeownertoclient" || subType === "changeownertoso";
    }

    /**
     * The leading identity columns. Per action too, because the pend-update action is the only one
     * whose ids may not be chases: with pend-id input the stored procedure never resolves a ChaseID,
     * so "Chase" and "Chase Key" would both be empty or (via the row's InputId fallback) show the
     * pend id under a chase heading.
     */
    private baseColumns(): GridColumnDefinition[] {
        if ((this.summary?.actionType || "").toLowerCase() === "pendupdateui") {
            return this.isPendIdInput
                ? [new GridColumnDefinition({ field: "chasePendId", header: "Pend Id" })]
                : [
                    new GridColumnDefinition({ field: "chase", header: "Chase Id" }),
                    new GridColumnDefinition({ field: "chasePendId", header: "Pend Id" }),
                ];
        }

        return [
            new GridColumnDefinition({ field: "chase", header: "Chase" }),
            new GridColumnDefinition({ field: "chaseKey", header: "Chase Key" }),
        ];
    }

    private createGrid(): void {
        const actionColumns = this.actionColumns();

        this.gridConfiguration = new GridConfiguration({
            columns: [
                ...this.baseColumns(),
                ...actionColumns,
                new GridColumnDefinition({ field: "validationStatus", header: "Validation Status" }),
                new GridColumnDefinition({ field: "comments", header: "Comments" }),
            ],
            pageSize: 25,
            showActionColumn: false,
            trackByField: "rowNum",
        });

        this.gridRequest = new GridRequest({
            url: this.service.getResultsUrl(this.jobId, this.phase),
        });
    }
}
